/**
 * Building blocks for the manuscript index view.
 *
 * The blocks are intentionally stateless — `ManuscriptIndex` keeps the
 * filter, reading-path, and appendix-expander state and passes the current
 * values plus setters down. Keeping them here lets the orchestrator stay
 * under the file-size budget while each block carries one responsibility.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { tierColor } from "./theme.js";
import type {
  Manuscript,
  ManuscriptAppendix,
  ManuscriptChapter,
  ManuscriptItemKind,
  ManuscriptViewerTheme,
} from "./types.js";

export interface PartBarProps {
  readonly parts: NonNullable<Manuscript["parts"]>;
  readonly partTotals: Record<string, number>;
  readonly totalWords: number;
  readonly filterPart: string | null;
  readonly setFilterPart: (value: string | null) => void;
  readonly theme: ManuscriptViewerTheme;
}

export function PartBar({
  parts,
  partTotals,
  totalWords,
  filterPart,
  setFilterPart,
  theme,
}: PartBarProps): React.JSX.Element {
  return (
    <div
      style={{
        display: "flex",
        borderRadius: 3,
        overflow: "hidden",
        border: `1px solid ${theme.border}`,
        height: 22,
      }}
    >
      {Object.entries(parts).map(([k, v]) => {
        const totalForPart = partTotals[k] ?? 0;
        const widthPct = totalWords > 0 ? (totalForPart / totalWords) * 100 : 0;
        if (widthPct <= 1) return null;
        return (
          <button
            key={k}
            type="button"
            onClick={() => {
              setFilterPart(filterPart === k ? null : k);
            }}
            title={`${v.label}: ${totalForPart.toLocaleString()} words`}
            style={{
              width: `${widthPct}%`,
              background: v.color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 9,
              color: "#fff",
              fontFamily: theme.serif,
              fontWeight: 650,
              letterSpacing: 0,
              whiteSpace: "nowrap",
              overflow: "hidden",
              borderRight: `1px solid ${theme.void}`,
              cursor: "pointer",
              border: 0,
            }}
          >
            {widthPct > 8 ? v.label : ""}
          </button>
        );
      })}
    </div>
  );
}

export interface ReadingPathsProps {
  readonly paths: NonNullable<Manuscript["readingPaths"]>;
  readonly selectedPath: string | null;
  readonly setSelectedPath: (value: string | null) => void;
  readonly pathWords: (key: string) => number;
  readonly theme: ManuscriptViewerTheme;
}

export function ReadingPaths({
  paths,
  selectedPath,
  setSelectedPath,
  pathWords,
  theme,
}: ReadingPathsProps): React.JSX.Element {
  const activePath = selectedPath !== null ? paths[selectedPath] : undefined;
  return (
    <div>
      <div
        style={{
          color: theme.muted,
          fontFamily: theme.serif,
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: 0,
          marginBottom: 8,
          textTransform: "uppercase",
        }}
      >
        Reading Paths
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {Object.entries(paths).map(([k, v]) => (
          <button
            key={k}
            type="button"
            onClick={() => {
              setSelectedPath(selectedPath === k ? null : k);
            }}
            style={{
              background: selectedPath === k ? theme.surface : "transparent",
              border: `1px solid ${selectedPath === k ? theme.muted : theme.border}`,
              borderRadius: 8,
              color: selectedPath === k ? theme.bright : theme.muted,
              cursor: "pointer",
              fontFamily: theme.serif,
              fontSize: 12,
              fontWeight: selectedPath === k ? 700 : 520,
              letterSpacing: 0,
              padding: "6px 12px",
            }}
          >
            {v.icon} {v.label}
          </button>
        ))}
      </div>
      {activePath !== undefined && selectedPath !== null ? (
        <p
          style={{
            marginTop: 6,
            fontSize: 12,
            color: theme.accentSoft,
            fontStyle: "italic",
          }}
        >
          {activePath.desc} — {activePath.ids.length} ch, {pathWords(selectedPath).toLocaleString()}{" "}
          words
        </p>
      ) : null}
    </div>
  );
}

export interface PartFilterChipsProps {
  readonly parts: NonNullable<Manuscript["parts"]>;
  readonly filterPart: string | null;
  readonly setFilterPart: (value: string | null) => void;
  readonly theme: ManuscriptViewerTheme;
}

export function PartFilterChips({
  parts,
  filterPart,
  setFilterPart,
  theme,
}: PartFilterChipsProps): React.JSX.Element {
  return (
    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
      <button
        type="button"
        onClick={() => {
          setFilterPart(null);
        }}
        style={{
          background: filterPart === null ? theme.surface : "transparent",
          border: `1px solid ${theme.border}`,
          borderRadius: 8,
          padding: "3px 8px",
          color: filterPart === null ? theme.bright : theme.muted,
          cursor: "pointer",
          fontFamily: theme.serif,
          fontSize: 11,
          fontWeight: filterPart === null ? 700 : 520,
        }}
      >
        All
      </button>
      {Object.entries(parts).map(([k, v]) => (
        <button
          key={k}
          type="button"
          onClick={() => {
            setFilterPart(filterPart === k ? null : k);
          }}
          style={{
            background: filterPart === k ? `${v.color}22` : "transparent",
            border: `1px solid ${filterPart === k ? v.color : theme.border}`,
            borderRadius: 8,
            padding: "3px 8px",
            color: filterPart === k ? v.color : theme.muted,
            cursor: "pointer",
            fontFamily: theme.serif,
            fontSize: 11,
            fontWeight: filterPart === k ? 700 : 520,
          }}
        >
          {v.label}
        </button>
      ))}
    </div>
  );
}

export interface ChapterRowsProps {
  readonly chapters: readonly ManuscriptChapter[];
  readonly filterPart: string | null;
  readonly highlighted: ReadonlySet<string> | null;
  readonly parts: Manuscript["parts"];
  readonly theme: ManuscriptViewerTheme;
  readonly onOpen: (item: ManuscriptChapter, kind: ManuscriptItemKind) => void;
}

export function ChapterRows({
  chapters,
  filterPart,
  highlighted,
  parts,
  theme,
  onOpen,
}: ChapterRowsProps): React.JSX.Element {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {chapters
        .filter((c) => filterPart === null || c.part === filterPart)
        .map((ch) => {
          const partColor =
            parts !== undefined && ch.part !== undefined
              ? (parts[ch.part]?.color ?? theme.muted)
              : theme.muted;
          const dim = highlighted !== null && !highlighted.has(ch.id);
          return (
            <button
              key={ch.id}
              type="button"
              className="csk-mv-row"
              onClick={() => {
                onOpen(ch, "chapter");
              }}
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(72px, 96px) minmax(0, 1fr) 64px 78px",
                alignItems: "center",
                gap: 8,
                padding: "8px 10px",
                borderTop: 0,
                borderRight: 0,
                borderBottom: 0,
                borderLeft: `3px solid ${dim ? theme.surfaceMuted : partColor}`,
                opacity: dim ? 0.3 : 1,
                borderRadius: "0 3px 3px 0",
                width: "100%",
                textAlign: "left",
                background: "transparent",
                cursor: "pointer",
              }}
            >
              <span
                title={`§${ch.id}`}
                style={{
                  fontFamily: theme.serif,
                  fontSize: 12,
                  color: partColor,
                  fontWeight: 700,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                §{ch.id}
              </span>
              <span
                style={{
                  color: dim ? theme.muted : theme.bright,
                  fontFamily: theme.serif,
                  fontSize: 14,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {ch.crown === true ? (
                  <span style={{ color: theme.accent, marginRight: 4 }} title="Strongest chapter">
                    ★
                  </span>
                ) : null}
                {ch.title}
              </span>
              <span
                style={{
                  fontFamily: theme.serif,
                  fontSize: 10,
                  color: theme.muted,
                  textAlign: "right",
                }}
              >
                {ch.words !== undefined ? ch.words.toLocaleString() : ""}
              </span>
              <span
                style={{
                  fontFamily: theme.serif,
                  fontSize: 9,
                  color: tierColor(ch.tier, theme),
                  textAlign: "right",
                  textTransform: "uppercase",
                }}
              >
                {ch.tier ?? ""}
              </span>
            </button>
          );
        })}
    </div>
  );
}

export interface AppendixListProps {
  readonly appendices: readonly ManuscriptAppendix[];
  readonly appWords: number;
  readonly showAppendices: boolean;
  readonly setShowAppendices: (value: boolean) => void;
  readonly theme: ManuscriptViewerTheme;
  readonly onOpen: (item: ManuscriptAppendix, kind: ManuscriptItemKind) => void;
}

export function AppendixList({
  appendices,
  appWords,
  showAppendices,
  setShowAppendices,
  theme,
  onOpen,
}: AppendixListProps): React.JSX.Element {
  return (
    <div style={{ borderTop: `1px solid ${theme.surfaceMuted}`, paddingTop: 12 }}>
      <button
        type="button"
        onClick={() => {
          setShowAppendices(!showAppendices);
        }}
        style={{
          background: "transparent",
          border: `1px solid ${theme.border}`,
          borderRadius: 8,
          padding: "6px 12px",
          color: theme.muted,
          cursor: "pointer",
          fontFamily: theme.serif,
          fontSize: 12,
          fontWeight: 650,
        }}
      >
        {showAppendices ? "▾" : "▸"} Appendices ({appendices.length}
        {appWords > 0 ? `, ${appWords.toLocaleString()} words` : ""})
      </button>
      {showAppendices ? (
        <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 1 }}>
          {appendices.map((a) => (
            <button
              key={a.id}
              type="button"
              className="csk-mv-row"
              onClick={() => {
                onOpen(a, "appendix");
              }}
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(72px, 96px) minmax(0, 1fr) 64px",
                alignItems: "center",
                gap: 8,
                padding: "6px 10px",
                borderTop: 0,
                borderRight: 0,
                borderBottom: 0,
                borderLeft: `3px solid ${theme.border}`,
                width: "100%",
                textAlign: "left",
                background: "transparent",
                cursor: "pointer",
              }}
            >
              <span
                title={a.id}
                style={{
                  color: theme.muted,
                  fontFamily: theme.serif,
                  fontSize: 11,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {a.id}
              </span>
              <span
                style={{
                  color: theme.text,
                  fontFamily: theme.serif,
                  fontSize: 13,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {a.title}
              </span>
              <span
                style={{
                  fontFamily: theme.serif,
                  fontSize: 10,
                  color: theme.muted,
                  textAlign: "right",
                }}
              >
                {a.words !== undefined ? a.words.toLocaleString() : ""}
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
